import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useMarkEnquiryLostMutation } from '@/api/endpoints';
import type { Enquiry } from '@/api/types';
import { Button } from '@/design/Button';
import { Field } from '@/design/Field';
import { Modal } from '@/design/Modal';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';

/**
 * Marking an enquiry lost, and saying why.
 *
 * This was a `window.prompt`, which is the one dialog in the app nobody
 * designed: it is unstyled, it cannot be corrected once dismissed, it has no
 * room to say what a useful answer looks like, and on a phone it sits over the
 * whole screen in the browser's own typeface. It also silently swallowed the
 * refusal — the mutation was fired and never awaited, so an enquiry that the
 * server declined to mark lost simply stayed as it was with nothing said.
 *
 * The reason is the most valuable field on the enquiries screen. "Too
 * expensive" forty times in a month is a pricing decision waiting to be made,
 * and "no vehicle free" forty times is a purchase. A prompt box collects that
 * badly; this asks for it properly.
 */

interface Props {
  readonly enquiry: Enquiry | null;
  readonly onClose: () => void;
}

/** Long enough to be a reason rather than a shrug. */
const SHORTEST_REASON = 4;

export function LostEnquiryModal({ enquiry, onClose }: Props): ReactElement {
  const [reason, setReason] = useState('');
  const [markLost, saving] = useMarkEnquiryLostMutation();
  const toast = useToast();
  const problems = readApiError(saving.error);

  // Cleared when a different enquiry is opened, so last time's reason is never
  // sitting in the box waiting to be attached to the wrong call.
  useEffect(() => setReason(''), [enquiry?.id]);

  async function save(): Promise<void> {
    if (enquiry === null) return;
    try {
      await markLost({ id: enquiry.id, lost_reason: reason.trim() }).unwrap();
      toast.done(`${enquiry.name} marked lost. It moves to the Lost tab with the reason on it.`);
      onClose();
    } catch {
      // The message stays in the modal: it is about what was typed in it.
    }
  }

  const tooShort = reason.trim().length < SHORTEST_REASON;

  return (
    <Modal
      open={enquiry !== null}
      onClose={onClose}
      title={enquiry === null ? 'Mark lost' : `Mark ${enquiry.name} lost`}
      note="Nothing is deleted. It moves to the Lost tab, with the reason on it."
      footer={
        <>
          <Button
            intent="primary"
            size="touch"
            type="submit"
            form="lost-enquiry-form"
            disabled={saving.isLoading || tooShort}
          >
            {saving.isLoading ? 'Saving' : 'Mark it lost'}
          </Button>
          <Button size="touch" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <form
        id="lost-enquiry-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        className="flex flex-col gap-4"
      >
        <Field label="Why was it lost?" required error={problems.fields['lost_reason']}>
          {({ id, describedBy }) => (
            <textarea
              id={id}
              aria-describedby={describedBy}
              rows={3}
              autoFocus
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Too expensive. Went with the place near the bus park."
              className="w-full rounded-sm border border-rule-strong bg-panel px-2.5 py-2 text-body"
            />
          )}
        </Field>

        <p className="text-small text-ink-muted">
          The same reason forty times in a month is worth acting on — too expensive is a price,
          nothing free is a vehicle to buy.
        </p>

        {problems.detail !== undefined && (
          <p className="font-medium text-alert" role="alert">
            {problems.detail}
          </p>
        )}
      </form>
    </Modal>
  );
}
